(() => {
  const vialCleanups = new WeakMap();

  function createVialDriver(frame) {
    const getDirectWindow = () => {
      try {
        const w = frame.contentWindow;
        if (w && w.__vial) return w;
      } catch (error) {
        return null;
      }
      return null;
    };

    return {
      setFixedTheta(theta) {
        const w = getDirectWindow();
        if (w) {
          w.__fixedTheta = theta;
          return;
        }

        frame.contentWindow?.postMessage(
          { type: 'imm-vial', cmd: 'setFixedTheta', value: theta },
          '*'
        );
      },
      clearFixedTheta() {
        const w = getDirectWindow();
        if (w) {
          delete w.__fixedTheta;
          return;
        }

        frame.contentWindow?.postMessage({ type: 'imm-vial', cmd: 'clearFixedTheta' }, '*');
      },
    };
  }

  function isFrameLoaded(frame) {
    try {
      return frame.contentDocument?.readyState === 'complete';
    } catch (error) {
      return false;
    }
  }

  function destroyVialTile(tile) {
    vialCleanups.get(tile)?.();
    vialCleanups.delete(tile);
    delete tile.dataset.vialInit;
  }

  function initVialTile(tile) {
    if (tile.dataset.vialInit === 'true') return;
    tile.dataset.vialInit = 'true';

    const frame = tile.querySelector('[data-spin-frame]');
    if (!frame) return;

    const driver = createVialDriver(frame);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hoverNone = window.matchMedia('(hover: none)');
    const state = { theta: 0, raf: 0, mode: 'idle', last: 0 };
    const controller = new AbortController();
    const { signal } = controller;
    let io = null;
    let loadTimeout = 0;

    const spin = (on) => {
      if (reducedMotion.matches) return;

      state.mode = on ? 'spin' : 'return';

      if (on) {
        tile.classList.add('is-vial-spinning');
      }

      if (state.raf) return;
      state.last = performance.now();
      const step = (now) => {
        const dt = Math.min(50, now - state.last);
        state.last = now;
        if (state.mode === 'spin') {
          state.theta = (state.theta + (dt / 1000 / 11) * 2 * Math.PI) % (2 * Math.PI);
          driver.setFixedTheta(state.theta);
        } else {
          let t = state.theta > Math.PI ? state.theta - 2 * Math.PI : state.theta;
          t *= Math.pow(0.08, dt / 1000);
          state.theta = ((t % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
          driver.setFixedTheta(state.theta);
          if (Math.abs(t) < 0.002) {
            state.theta = 0;
            driver.clearFixedTheta();
            tile.classList.remove('is-vial-spinning');
            state.raf = 0;
            return;
          }
        }
        state.raf = requestAnimationFrame(step);
      };
      state.raf = requestAnimationFrame(step);
    };

    const markReady = () => {
      tile.classList.add('is-vial-ready');
    };

    const cleanup = () => {
      if (state.raf) cancelAnimationFrame(state.raf);
      state.raf = 0;
      state.mode = 'idle';
      io?.disconnect();
      io = null;
      window.clearTimeout(loadTimeout);
      controller.abort();
      frame.removeEventListener('load', markReady);
    };

    vialCleanups.set(tile, cleanup);

    if (reducedMotion.matches) {
      markReady();
      return;
    }

    tile.addEventListener('mouseenter', () => spin(true), { signal });
    tile.addEventListener('mouseleave', () => spin(false), { signal });
    tile.addEventListener('touchstart', () => spin(true), { passive: true, signal });
    tile.addEventListener('touchend', () => spin(false), { passive: true, signal });
    tile.addEventListener('touchcancel', () => spin(false), { passive: true, signal });

    if (isFrameLoaded(frame)) {
      markReady();
    } else {
      frame.addEventListener('load', markReady, { once: true });
    }

    loadTimeout = window.setTimeout(() => {
      if (!tile.classList.contains('is-vial-ready')) markReady();
    }, 1200);

    if (hoverNone.matches && 'IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio > 0.6) spin(true);
          else spin(false);
        }
      }, { threshold: [0, 0.6, 1] });
      io.observe(tile);
    }
  }

  function init() {
    for (const tile of document.querySelectorAll('[data-homepage-v2-hero] .homepage-v2-hero__tile--vial')) {
      initVialTile(tile);
    }
  }

  function unload(event) {
    for (const tile of event.target.querySelectorAll('.homepage-v2-hero__tile--vial')) {
      destroyVialTile(tile);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', unload);
})();
